/**
 * Book.js — FadeFlow multi-step booking flow
 *
 * Steps: service → date/time (calendar) → confirm
 * Calendar shows full month view with availability indicators.
 * Selecting a date reveals time slots inline below the calendar.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, onSnapshot, query, where, addDoc, serverTimestamp, getDocs,
} from 'firebase/firestore';

// 8:00 AM → 6:30 PM in 30-min slots
const HOURS = Array.from({ length: 22 }, (_, i) => {
  const h = 8 + Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_LABELS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function phoneKey(phone) {
  const d = (phone || '').replace(/\D/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function isoStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function todayLocalStr() {
  return isoStr(new Date());
}

/** Same biweekly parity used by the barber app */
function weekParity(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  return Math.floor(d.getTime() / (7 * 24 * 60 * 60 * 1000)) % 2;
}

/** Build 6-row × 7-col grid for a given month (Sun-first) */
function buildCalGrid(year, month) {
  const firstDow   = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMon  = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMon; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** Google Calendar URL */
function googleCalUrl(serviceName, shopName, shopAddress, date, time, duration) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, m]     = time.split(':').map(Number);
  const start = new Date(y, mo - 1, d, h, m);
  const end   = new Date(start.getTime() + duration * 60000);
  const fmt = dt =>
    `${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,'0')}${String(dt.getDate()).padStart(2,'0')}T${String(dt.getHours()).padStart(2,'0')}${String(dt.getMinutes()).padStart(2,'0')}00`;
  const params = new URLSearchParams({
    action:   'TEMPLATE',
    text:     `✂️ ${serviceName} @ ${shopName}`,
    dates:    `${fmt(start)}/${fmt(end)}`,
    details:  shopAddress || shopName,
    location: shopAddress || '',
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

/** Build and trigger .ics download */
function downloadICS(serviceName, shopName, shopAddress, date, time, duration) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, m]     = time.split(':').map(Number);
  const start = new Date(y, mo - 1, d, h, m);
  const end   = new Date(start.getTime() + duration * 60000);
  const fmt = dt =>
    `${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,'0')}${String(dt.getDate()).padStart(2,'0')}T${String(dt.getHours()).padStart(2,'0')}${String(dt.getMinutes()).padStart(2,'0')}00`;
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//FadeFlow//EN',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
    `SUMMARY:✂️ ${serviceName} @ ${shopName}`,
    `LOCATION:${shopAddress || ''}`,
    `DESCRIPTION:${shopName}`,
    `UID:${Date.now()}@fadeflow.app`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'appointment.ics'; a.click();
  URL.revokeObjectURL(url);
}

export default function Book({ shop, shopId, slug }) {
  const navigate  = useNavigate();
  const phone     = auth.currentUser?.phoneNumber || '';
  const clientId  = phoneKey(phone);
  const name      = localStorage.getItem('ff_name') || '';

  const now = new Date();
  const [services,       setServices]       = useState([]);
  const [bookedMap,      setBookedMap]      = useState({});
  const [service,        setService]        = useState(null);
  const [date,           setDate]           = useState('');
  const [time,           setTime]           = useState('');
  const [step,           setStep]           = useState('service');
  const [saving,         setSaving]         = useState(false);
  const [done,           setDone]           = useState(false);
  const [joinWaitlist,   setJoinWaitlist]   = useState(false);
  const [waitlistTime,   setWaitlistTime]   = useState('');
  const [waitlistSaving, setWaitlistSaving] = useState(false);
  const [waitlistDone,   setWaitlistDone]   = useState(false);

  const [recurringSlots, setRecurringSlots] = useState([]);

  // Calendar state
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const today = todayLocalStr();

  // Live services
  useEffect(() => {
    if (!shopId) return;
    const unsub = onSnapshot(collection(db, 'shops', shopId, 'services'), snap => {
      const svcs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setServices(svcs);
    });
    return unsub;
  }, [shopId]);

  // Load recurring (regular) slots — blocks those times for walk-in customers
  useEffect(() => {
    if (!shopId) return;
    getDocs(collection(db, 'shops', shopId, 'recurring'))
      .then(snap => setRecurringSlots(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [shopId]);

  // Live booked slots (future only)
  useEffect(() => {
    if (!shopId) return;
    const q = query(
      collection(db, 'shops', shopId, 'appointments'),
      where('date', '>=', today),
      where('status', 'in', ['scheduled', 'pending'])
    );
    const unsub = onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const a = d.data();
        if (!map[a.date]) map[a.date] = [];
        map[a.date].push(a.time);
      });
      setBookedMap(map);
    });
    return unsub;
  }, [shopId]);

  /** True if a recurring regular-client rule blocks this slot */
  function isRecurringBlocked(isoDate, t) {
    if (!isoDate) return false;
    const dow    = new Date(isoDate + 'T12:00:00').getDay();
    const parity = weekParity(isoDate);
    return recurringSlots.some(slot =>
      slot.dayOfWeek === dow &&
      slot.time === t &&
      (slot.frequency === 'weekly' ||
       (slot.frequency === 'biweekly' && slot.weekParity === parity))
    );
  }

  const takenSlots     = bookedMap[date] || [];
  const availableSlots = HOURS.filter(h => !takenSlots.includes(h) && !isRecurringBlocked(date, h));
  const fullSlots      = HOURS.filter(h =>  takenSlots.includes(h) || isRecurringBlocked(date, h));

  function prevMonth() {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
    setDate(''); setTime('');
  }
  function nextMonth() {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
    setDate(''); setTime('');
  }

  // Don't allow navigating to past months
  const isPrevDisabled = calYear === now.getFullYear() && calMonth === now.getMonth();
  // Don't allow booking more than 3 months out
  const isNextDisabled = calYear > now.getFullYear() ||
    (calYear === now.getFullYear() && calMonth >= now.getMonth() + 3);

  async function book() {
    if (!service || !date || !time) return;
    setSaving(true);
    try {
      const phone10 = clientId.slice(-10);
      const status  = shop?.autoConfirmBookings ? 'scheduled' : 'pending';
      await addDoc(collection(db, 'shops', shopId, 'appointments'), {
        client_name:  name,
        client_phone: phone10,
        service_name: service.name,
        duration:     Number(service.duration || 60),
        price:        Number(service.price    || 0),
        date,
        time,
        status,
        source:    'customer_web',
        createdAt: serverTimestamp(),
      });

      // Notify the barber via Expo push if they have a token
      if (shop?.expoPushToken) {
        const d = new Date(date + 'T12:00:00');
        const dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const timeLabel = formatTime(time);
        fetch('https://exp.host/--/expoapi/v2/push/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to:    shop.expoPushToken,
            title: status === 'scheduled' ? '✅ New Booking!' : '📅 New Booking Request',
            body:  `${name} booked ${service.name} · ${dateLabel} at ${timeLabel}`,
            data:  { screen: 'Today' },
          }),
        }).catch(() => {}); // fire and forget — don't block the booking
      }

      setDone(true);
    } catch (e) {
      console.error(e);
      alert('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function joinWaitlistForSlot() {
    setWaitlistSaving(true);
    try {
      const phone10 = clientId.slice(-10);
      await addDoc(collection(db, 'shops', shopId, 'waitlist'), {
        client_name:  name,
        client_phone: phone10,
        date,
        time:         waitlistTime,
        createdAt:    serverTimestamp(),
      });
      setWaitlistDone(true);
      setJoinWaitlist(false);
    } catch (e) {
      console.error(e);
      alert('Could not join waitlist. Try again.');
    } finally {
      setWaitlistSaving(false);
    }
  }

  // ── Booking confirmed ────────────────────────────────────────────────────────
  if (done) {
    const d = new Date(date + 'T12:00:00');
    const dateLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    return (
      <div style={{ padding: 24, paddingTop: 80, textAlign: 'center', paddingBottom: 80 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>
          {shop?.autoConfirmBookings ? 'Booking Confirmed!' : 'Request Sent!'}
        </div>
        <div style={{ fontSize: 15, color: 'var(--subtext)', lineHeight: 1.6, marginBottom: 32 }}>
          {shop?.autoConfirmBookings
            ? `Your appointment with ${shop?.name || 'your barber'} is confirmed. See you there! 🎉`
            : `${shop?.name || 'Your barber'} will confirm your appointment shortly. You'll see it on your home screen once confirmed.`
          }
        </div>

        <div style={{
          background: 'var(--card)', borderRadius: 18, border: '1px solid var(--border)',
          padding: 20, marginBottom: 24, textAlign: 'left',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>📅 Add to your calendar</div>
          <div style={{ fontSize: 13, color: 'var(--subtext)', marginBottom: 16 }}>
            {service?.name} · {dateLabel} at {formatTime(time)}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <a
              href={googleCalUrl(service?.name || '', shop?.name || 'Barber', shop?.address || '', date, time, service?.duration || 60)}
              target="_blank" rel="noopener noreferrer"
              style={{
                flex: 1, background: 'var(--card)', border: '2px solid var(--border)',
                borderRadius: 12, padding: '12px 8px', fontSize: 14, fontWeight: 700,
                color: 'var(--text)', textAlign: 'center', textDecoration: 'none',
              }}
            >🗓️ Google Calendar</a>
            <button
              onClick={() => downloadICS(service?.name || '', shop?.name || 'Barber', shop?.address || '', date, time, service?.duration || 60)}
              style={{
                flex: 1, background: 'var(--card)', border: '2px solid var(--border)',
                borderRadius: 12, padding: '12px 8px', fontSize: 14, fontWeight: 700,
                color: 'var(--text)', cursor: 'pointer',
              }}
            >🍎 Apple Calendar</button>
          </div>
        </div>

        <button
          onClick={() => navigate(`/book/${slug}`)}
          style={{
            background: 'var(--accent)', borderRadius: 16, padding: '16px 40px',
            fontSize: 17, fontWeight: 900, color: '#000', border: 'none', cursor: 'pointer', width: '100%',
          }}
        >Back to Home</button>
      </div>
    );
  }

  // ── Waitlist join ────────────────────────────────────────────────────────────
  if (joinWaitlist) return (
    <div style={{ padding: 24, paddingTop: 80, textAlign: 'center', paddingBottom: 80 }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>📋</div>
      <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Join Waitlist?</div>
      <div style={{ fontSize: 15, color: 'var(--subtext)', lineHeight: 1.6, marginBottom: 32 }}>
        {formatTime(waitlistTime)} on {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} is taken.
        Join the waitlist and you'll be notified if it opens up.
      </div>
      {waitlistDone ? (
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--accent)', marginBottom: 24 }}>✓ You're on the waitlist!</div>
      ) : (
        <button
          onClick={joinWaitlistForSlot}
          disabled={waitlistSaving}
          style={{
            width: '100%', background: 'var(--accent)', borderRadius: 14, padding: 18,
            fontSize: 17, fontWeight: 900, color: '#000', border: 'none', cursor: 'pointer',
            opacity: waitlistSaving ? 0.5 : 1, marginBottom: 12,
          }}
        >{waitlistSaving ? 'Joining…' : '📋 Join Waitlist'}</button>
      )}
      <button
        onClick={() => { setJoinWaitlist(false); setWaitlistDone(false); }}
        style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, border: 'none', cursor: 'pointer', padding: '12px 0', width: '100%' }}
      >← Back</button>
    </div>
  );

  // ── Step: Service ────────────────────────────────────────────────────────────
  if (step === 'service') return (
    <div style={{ padding: 24, paddingTop: 48, paddingBottom: 80 }}>
      <button
        onClick={() => navigate(`/book/${slug}`)}
        style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, marginBottom: 16, padding: 0, border: 'none', cursor: 'pointer' }}
      >← Back</button>
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>Pick a Service</div>
      <div style={{ fontSize: 14, color: 'var(--subtext)', marginBottom: 24 }}>What are you coming in for?</div>

      {services.length === 0 && (
        <div style={{ color: 'var(--subtext)', textAlign: 'center', paddingTop: 40 }}>Loading services…</div>
      )}

      {services.map(s => (
        <button
          key={s.id}
          onClick={() => { setService(s); setStep('datetime'); }}
          style={{
            width: '100%', background: 'var(--card)', borderRadius: 16,
            border: `2px solid ${service?.id === s.id ? 'var(--accent)' : 'var(--border)'}`,
            padding: '18px 20px', marginBottom: 10, cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{s.name}</div>
            {s.duration && <div style={{ fontSize: 13, color: 'var(--subtext)', marginTop: 3 }}>{s.duration} min</div>}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)' }}>${Number(s.price || 0).toFixed(0)}</div>
        </button>
      ))}
    </div>
  );

  // ── Step: Date & Time (calendar view) ────────────────────────────────────────
  if (step === 'datetime') {
    const cells = buildCalGrid(calYear, calMonth);

    return (
      <div style={{ padding: 24, paddingTop: 48, paddingBottom: date ? 300 : 80 }}>
        <button
          onClick={() => setStep('service')}
          style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, marginBottom: 16, padding: 0, border: 'none', cursor: 'pointer' }}
        >← Back</button>
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 2 }}>Pick a Date</div>
        <div style={{ fontSize: 14, color: 'var(--subtext)', marginBottom: 20 }}>{service?.name}</div>

        {/* ── Calendar card ── */}
        <div style={{
          background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)',
          padding: '20px 16px',
        }}>
          {/* Month navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <button
              onClick={prevMonth}
              disabled={isPrevDisabled}
              style={{
                width: 36, height: 36, borderRadius: 18,
                background: isPrevDisabled ? 'transparent' : 'var(--border)',
                border: 'none', fontSize: 20, color: isPrevDisabled ? 'var(--border)' : 'var(--text)',
                cursor: isPrevDisabled ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >‹</button>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>
              {MONTH_NAMES[calMonth]} {calYear}
            </div>
            <button
              onClick={nextMonth}
              disabled={isNextDisabled}
              style={{
                width: 36, height: 36, borderRadius: 18,
                background: isNextDisabled ? 'transparent' : 'var(--border)',
                border: 'none', fontSize: 20, color: isNextDisabled ? 'var(--border)' : 'var(--text)',
                cursor: isNextDisabled ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >›</button>
          </div>

          {/* Day of week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 }}>
            {DAY_LABELS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--subtext)', paddingBottom: 6 }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {cells.map((day, i) => {
              if (!day) return <div key={i} />;

              const iso           = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isPast        = iso < today;
              const isToday       = iso === today;
              const isSelected    = iso === date;
              const bookedTimes   = bookedMap[iso] || [];
              const available     = HOURS.filter(h => !bookedTimes.includes(h) && !isRecurringBlocked(iso, h)).length;
              const taken         = HOURS.length - available;
              const isFullyBooked = available <= 0;

              return (
                <button
                  key={i}
                  onClick={() => { if (!isPast && !isFullyBooked) { setDate(iso); setTime(''); } }}
                  style={{
                    aspectRatio: '1', borderRadius: 10,
                    border: isToday && !isSelected ? '2px solid var(--accent)' : '2px solid transparent',
                    background: isSelected
                      ? 'var(--accent)'
                      : isToday && !isSelected ? 'var(--accent-dim)' : 'transparent',
                    cursor: isPast || isFullyBooked ? 'default' : 'pointer',
                    opacity: isPast ? 0.2 : isFullyBooked ? 0.4 : 1,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 3, padding: '4px 2px',
                  }}
                >
                  <span style={{
                    fontSize: 15, fontWeight: isSelected || isToday ? 800 : 500,
                    color: isSelected ? '#000' : 'var(--text)', lineHeight: 1,
                  }}>
                    {day}
                  </span>
                  {!isPast && (
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: isSelected
                        ? 'rgba(0,0,0,0.4)'
                        : isFullyBooked ? 'var(--danger)'
                        : taken > 0 ? 'var(--accent)' : 'transparent',
                      display: 'block',
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 14, justifyContent: 'center' }}>
            {[
              { color: 'var(--accent)', label: 'Some booked' },
              { color: 'var(--danger)', label: 'Full' },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                <span style={{ fontSize: 11, color: 'var(--subtext)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hint when nothing selected */}
        {!date && (
          <div style={{ textAlign: 'center', color: 'var(--subtext)', fontSize: 14, paddingTop: 20 }}>
            Tap a date to see available times
          </div>
        )}

        {/* ── Bottom sheet — slides up when date is selected ── */}
        {date && (
          <div style={{
            position: 'fixed',
            bottom: 60,        // sit right above the nav bar
            left: 0, right: 0,
            maxWidth: 480,
            margin: '0 auto',
            zIndex: 200,
          }}>
            <div style={{
              background: 'var(--card)',
              borderTop: '1px solid var(--border)',
              borderRadius: '22px 22px 0 0',
              padding: '18px 20px 28px',
              boxShadow: '0 -16px 48px rgba(0,0,0,0.55)',
            }}>
              {/* Sheet header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </div>
                <button
                  onClick={() => { setDate(''); setTime(''); }}
                  style={{
                    background: 'var(--border)', border: 'none', color: 'var(--subtext)',
                    fontSize: 16, fontWeight: 700, cursor: 'pointer',
                    width: 28, height: 28, borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >×</button>
              </div>

              {availableSlots.length === 0 && fullSlots.length === 0 && (
                <div style={{ color: 'var(--subtext)', fontSize: 14, textAlign: 'center', padding: '12px 0 16px' }}>
                  No times available on this day
                </div>
              )}

              {/* Time chips — horizontal scroll, no page scroll needed */}
              {(availableSlots.length > 0 || fullSlots.length > 0) && (
                <div style={{ overflowX: 'auto', marginBottom: 16, paddingBottom: 4 }}>
                  <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                    {availableSlots.map(h => (
                      <button
                        key={h}
                        onClick={() => setTime(h)}
                        style={{
                          padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                          border: `2px solid ${time === h ? 'var(--accent)' : 'var(--border)'}`,
                          background: time === h ? 'var(--accent)' : 'var(--input-bg)',
                          color: time === h ? '#000' : 'var(--text)',
                          cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                      >{formatTime(h)}</button>
                    ))}
                    {fullSlots.map(h => (
                      <button
                        key={h}
                        onClick={() => { setWaitlistTime(h); setJoinWaitlist(true); }}
                        style={{
                          padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                          border: '2px solid var(--border)', background: 'var(--input-bg)',
                          color: 'var(--subtext)', cursor: 'pointer', opacity: 0.45,
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                      >{formatTime(h)} 📋</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Next button */}
              <button
                onClick={() => time && setStep('confirm')}
                disabled={!time}
                style={{
                  width: '100%', background: 'var(--accent)', borderRadius: 14,
                  padding: 16, fontSize: 17, fontWeight: 900, color: '#000',
                  opacity: time ? 1 : 0.3, border: 'none', cursor: time ? 'pointer' : 'not-allowed',
                }}
              >Next →</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Step: Confirm ────────────────────────────────────────────────────────────
  const dateLabel = date
    ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  return (
    <div style={{ padding: 24, paddingTop: 48, paddingBottom: 80 }}>
      <button
        onClick={() => setStep('datetime')}
        style={{ background: 'none', color: 'var(--subtext)', fontSize: 15, marginBottom: 16, padding: 0, border: 'none', cursor: 'pointer' }}
      >← Back</button>
      <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 24 }}>Confirm Booking</div>

      <div style={{
        background: 'var(--card)', borderRadius: 18, border: '1px solid var(--border)',
        padding: 22, marginBottom: 24,
      }}>
        {[
          ['Service',  service?.name],
          ['Date',     dateLabel],
          ['Time',     formatTime(time)],
          ['Duration', service?.duration ? `${service.duration} min` : null],
          ['Price',    `$${Number(service?.price || 0).toFixed(0)}`],
          ['Name',     name],
        ].filter(([, v]) => v).map(([label, value], i, arr) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between',
            paddingTop: 14, paddingBottom: 14,
            borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <span style={{ color: 'var(--subtext)', fontSize: 15 }}>{label}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: label === 'Price' ? 'var(--accent)' : 'var(--text)' }}>
              {value}
            </span>
          </div>
        ))}
      </div>

      <div style={{
        background: 'var(--accent-dim)', borderRadius: 12, border: '1px solid var(--accent)',
        padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent)', lineHeight: 1.5,
      }}>
        {shop?.autoConfirmBookings
          ? `⚡ This appointment will confirm instantly.`
          : `📅 ${shop?.name || 'Your barber'} will confirm your appointment shortly after receiving your request.`
        }
      </div>

      <button
        onClick={book}
        disabled={saving}
        style={{
          width: '100%', background: 'var(--accent)', borderRadius: 14,
          padding: 20, fontSize: 17, fontWeight: 900, color: '#000',
          opacity: saving ? 0.5 : 1, border: 'none', cursor: 'pointer',
        }}
      >{saving ? 'Booking…' : shop?.autoConfirmBookings ? '✂️  Confirm Booking' : '✂️  Request Appointment'}</button>
    </div>
  );
}
